import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Alert,
  IconButton,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { auth } from '../services/auth';
import { useLocation } from 'react-router-dom';

const steps = ['Account Details', 'Referral (Optional)'];

const Register: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) setReferralCode(ref);
  }, [location.search]);

  const validateStep = () => {
    if (activeStep === 0) {
      if (!email) return setError('Email is required'), false;
      if (!password) return setError('Password is required'), false;
      if (password !== confirmPassword) return setError('Passwords do not match'), false;
      if (password.length < 6) return setError('Password must be at least 6 characters'), false;
      if (!email.includes('@')) return setError('Please enter a valid email address'), false;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (validateStep()) setActiveStep(1);
  };

  const handleBack = () => {
    setActiveStep(0);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await auth.register(email, password, fullName, referralCode);
      setEmailSent(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ SUCCESS STATE (CORRECT)
  if (emailSent) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              📩 Check your email
            </Typography>

            <Typography color="text.secondary" mb={2}>
              We sent a verification link to:
            </Typography>

            <Typography fontWeight="bold" mb={3}>
              {email}
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={4}>
              Click the link in your email to activate your account.
            </Typography>

            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{ mb: 2 }}
            >
              Go to Login
            </Button>

            <Button
              variant="text"
              disabled={resendLoading}
              onClick={async () => {
                setResendLoading(true);
                await auth.resendVerification(email);
                setTimeout(() => setResendLoading(false), 5000);
              }}
            >
              {resendLoading ? 'Sending...' : 'Resend email'}
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // ✅ NORMAL REGISTER FORM (UNCHANGED LOGIC)
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Create Account
            </Typography>
            <Typography color="text.secondary">
              Start downloading videos for free
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            {activeStep === 0 ? (
              <Box>
                <TextField fullWidth label="Full Name (Optional)" value={fullName} onChange={(e) => setFullName(e.target.value)} margin="normal" />
                <TextField fullWidth label="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" required />
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField fullWidth label="Confirm Password" type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} margin="normal" required />
                <Button fullWidth variant="contained" size="large" onClick={handleNext} sx={{ mt: 3 }}>
                  Continue
                </Button>
              </Box>
            ) : (
              <Box>
                <TextField fullWidth label="Referral Code (Optional)" value={referralCode} onChange={(e) => setReferralCode(e.target.value)} margin="normal" />
                {referralCode && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    🎉 Referral code applied!
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button fullWidth variant="outlined" onClick={handleBack}>Back</Button>
                  <Button fullWidth variant="contained" type="submit">
                    {loading ? 'Creating Account...' : 'Sign Up'}
                  </Button>
                </Box>
              </Box>
            )}
          </form>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2">
              Already have an account?{' '}
              <Link component={RouterLink} to="/login">
                Sign in
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Register;